import * as E from 'fp-ts/Either';
import { identity, pipe } from 'fp-ts/lib/function';
import { Api } from '@fixtuur/backpack';
import * as Core from '../../store/core';
import { useDispatch, useSelector } from '../../store/redux';
import * as Selectors from '../../store/selectors';
import { Configuration } from '@fixtuur/gavel-client';

import { logInfo } from '../../store/actions/log';
import { Effect } from 'effect';
import { useStellaLayer } from '../useStellaLayer';
import { useEffectful } from '../useEffectful';
import { useMemo } from 'preact/hooks';

export const useConfiguratorDefinitions = ():
    | Array<Api.ConfiguratorDefinition.ConfiguratorDefinition>
    | undefined => {
    const stellaLayer = useStellaLayer();
    const currentProductId = useSelector(Selectors.Initialisation.currentInspirationProductId);
    const tenant = useSelector(Selectors.Core.tenant);
    const locale = useSelector(Selectors.Core.locale);
    const dispatch = useDispatch();

    const spaceIds = useEffectful(
        () =>
            tenant && currentProductId
                ? Configuration.ConfigurationService.pipe(
                      Effect.andThen(config =>
                          config.spaces(tenant, { productId: currentProductId }),
                      ),
                      Effect.map(res => res.spaces),
                      Effect.catchAll(e => {
                          dispatch(
                              Core.error({
                                  message: e.message,
                                  context: {
                                      type: 'log',
                                      location: 'useConfiguratorDefinition',
                                  },
                              }),
                          );
                          return Effect.succeed([]);
                      }),
                      Effect.provide(stellaLayer),
                  )
                : Effect.succeed(null),
        [tenant, currentProductId],
    ).result;

    const configuratorDefinitions =
        useMemo((): Array<Api.ConfiguratorDefinition.ConfiguratorDefinition> => {
            if (!tenant || !spaceIds || !spaceIds.length) {
                return [];
            }

            // This entire block is slightly awkward because we're not handling errors
            // properly with TaskThese. In our defence TaskThese is a bit of a pain to use
            // and we don't need that power just yet.
            const configuratorDefinitions = spaceIds
                .map(id =>
                    pipe(
                        Api.ConfiguratorDefinition.getConfiguratorDefinitionBySpaceId(
                            id,
                            tenant,
                            locale ?? 'en-GB',
                        ),
                        E.match(() => {
                            dispatch(
                                logInfo(`configurator definition not found for space id: ${id}`),
                            );
                            return undefined;
                        }, identity),
                    ),
                )
                .filter(
                    (
                        c: Api.ConfiguratorDefinition.ConfiguratorDefinition | undefined,
                    ): c is Api.ConfiguratorDefinition.ConfiguratorDefinition => c !== undefined,
                );

            return configuratorDefinitions;
        }, [spaceIds, tenant]);

    return configuratorDefinitions;
};
